import { FocusEvent, HTMLAttributes, useRef } from 'react';

interface FocusWithinProps {
  /** Called when the target element or a descendant receives focus. */
  onFocusWithin?: (e: FocusEvent) => void;
  /** Called when the target element and all descendants lose focus. */
  onBlurWithin?: (e: FocusEvent) => void;
  /** Called when the the focus within state changes. */
  onFocusWithinChange?: (isFocusWithin: boolean) => void;
  /** Whether the focus within events should be disabled. */
  isDisabled?: boolean;
}

interface FocusWithinResult {
  /** Props to spread onto the target element. */
  focusWithinProps: HTMLAttributes<HTMLElement>;
}

/**
 * Handles focus events for the target and its descendants. Focus is "within"
 * an element when either the element itself or a descendant element has focus.
 * This is similar to the :focus-within pseudo class in CSS.
 *
 * @example
 *   const [hasFocus, setHasFocus] = useState(false)
 *   const { focusWithinProps } = useFocusWithin({
 *     onFocusWithinChange: (focus) => setHasFocus(focus),
 *   })
 *   return <div {...focusWithinProps}>Do stuff with focused state</div>
 */
export function useFocusWithin(props: FocusWithinProps): FocusWithinResult {
  const state = useRef({
    isFocusWithin: false,
  }).current;

  if (props.isDisabled) {
    return { focusWithinProps: {} };
  }

  const onFocus = (e: FocusEvent) => {
    if (!state.isFocusWithin) {
      if (props.onFocusWithin) {
        props.onFocusWithin(e);
      }

      if (props.onFocusWithinChange) {
        props.onFocusWithinChange(true);
      }

      state.isFocusWithin = true;
    }
  };

  const onBlur = (e: FocusEvent) => {
    /**
     * Don't trigger onBlurWithin and then immediately onFocusWithin again when
     * moving focus inside the element. Only trigger if the currentTarget
     * doesn't include the relatedTarget (where focus is moving).
     */
    if (
      state.isFocusWithin &&
      !e.currentTarget.contains(e.relatedTarget as HTMLElement)
    ) {
      if (props.onBlurWithin) {
        props.onBlurWithin(e);
      }

      if (props.onFocusWithinChange) {
        props.onFocusWithinChange(false);
      }

      state.isFocusWithin = false;
    }
  };

  return {
    focusWithinProps: {
      onFocus: onFocus,
      onBlur: onBlur,
    },
  };
}
